import { chatApis } from '@/configs/apis/chat';
import { defaultMsgExpireDuration } from '@/configs/options';
import { useSessionContext } from '@/contexts/session/context';
import type {
  ChatChatId,
  ChatParams,
  CreateSingleChatParams,
} from '../hooks/useChats/types';
import { ChatCategory } from '../hooks/useChats/types';
import { serverTime } from '../hooks/useSystemTime/utils';
import type { ChatContextHooksFull, ChatContextHooksProps } from '../../context';

export type UseSingleChatsValueResult = ReturnType<typeof useSingleChatsValue>;

export function useSingleChatsValue({ hooks }: ChatContextHooksProps) {
  const {
    userSettings: { userSettingsState },
  } = useSessionContext();
  const { singleEncryption } = hooks;

  const initSingleChat = useCallback(
    async (params: ChatParams) => {
      const {
        chatsValue: { initChat },
      } = hooks as ChatContextHooksFull;
      return initChat(params, async (chatPartial) => {
        const { chat_id } = chatPartial;
        if (chat_id) {
          const user = await chatApis.getChatInfo(chat_id);
          if (user) {
            chatPartial.category = ChatCategory.SINGLE;
            chatPartial.avatar = user.avatar;
            chatPartial.receiver_id = user.userId;
            chatPartial.name = user.name;
            chatPartial.create_at = user.createdAt || serverTime.time;
            chatPartial.update_at = chatPartial.create_at;
            chatPartial.expire_duration =
              user.expire_duration || defaultMsgExpireDuration;
            await singleEncryption.getTargetChatPubKeys(chatPartial.receiver_id);
          }
        }
      });
    },
    [hooks, singleEncryption]
  );

  const createSingleChat = useCallback(
    async ({ userId, ...other }: CreateSingleChatParams) => {
      const {
        chats: { getChat },
        chatsValue: { createChat },
      } = hooks as ChatContextHooksFull;
      // TODO amo: Find chat by user id
      const oldChat = await getChat({ chat_id: 'P_' + userId });

      if (oldChat) {
        return oldChat;
      } else {
        const { chat_id } = await chatApis.createChat({
          user_id: userId,
          expire_duration:
            userSettingsState.expire_duration || defaultMsgExpireDuration,
        });
        return createChat({ chat_id, ...other }, initSingleChat);
      }
    },
    [hooks, initSingleChat, userSettingsState.expire_duration]
  );

  const loadSingleChatByChatId = useCallback(
    async (params: ChatParams) => {
      const {
        chatsValue: { loadChatByChatId },
        singleEncryption,
      } = hooks as ChatContextHooksFull;
      return loadChatByChatId(params, initSingleChat, (chatPartial) => {
        return singleEncryption.getTargetChatPubKeys(chatPartial.receiver_id!);
      });
    },
    [hooks, initSingleChat]
  );

  const syncSingleChat = useCallback(
    async (chat_id: ChatChatId) => {
      const user = await chatApis.getChatInfo(chat_id);
      if (user?.expire_duration) {
        const {
          chats: { updateChats },
        } = hooks as ChatContextHooksFull;
        await updateChats([
          {
            chat_id,
            avatar: user.avatar,
            name: user.name,
            expire_duration: user.expire_duration,
          },
        ]);
      }
    },
    [hooks]
  );

  return useMemo(
    () => ({
      initSingleChat,
      createSingleChat,
      loadSingleChatByChatId,
      syncSingleChat,
    }),
    [createSingleChat, initSingleChat, loadSingleChatByChatId, syncSingleChat]
  );
}
